import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { ShippingAddress } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { CheckoutEmailStep } from "./checkout/CheckoutEmailStep";
import { CheckoutShippingStep } from "./checkout/CheckoutShippingStep";
import { CheckoutPaymentStep } from "./checkout/CheckoutPaymentStep";

interface Product {
  _id: string;
  name?: string;
  price: number;
}

interface CheckoutFlowProps {
  checkoutId: string;
  products: Product[];
  stripe: {
    publishableKey: string;
    accountId?: string;
  } | null;
  checkoutSettings: {
    tipping?: {
      enabled: boolean;
      amount1?: number;
      amount2?: number;
      amount3?: number;
    };
  } | null;
  artistName?: string;
}

const STEP_MAP = { email: 1, shipping: 2, payment: 3 } as const;

export function CheckoutFlow({ checkoutId, products, stripe, checkoutSettings, artistName }: CheckoutFlowProps) {
  const { step: stepParam } = useParams<{ step: string }>();
  const navigate = useNavigate();
  const step = stepParam ? (STEP_MAP[stepParam as keyof typeof STEP_MAP] ?? 1) : 1;
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [customerEmail, setCustomerEmail] = useState("");
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress | null>(null);
  const [isAddressComplete, setIsAddressComplete] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stripePromise = useMemo(() => {
    if (!stripe?.publishableKey) return null;
    const options: { stripeAccount?: string } = {};
    if (stripe.accountId) options.stripeAccount = stripe.accountId;
    return loadStripe(stripe.publishableKey, options);
  }, [stripe]);

  useEffect(() => {
    if (products.length > 0) {
      const initialQuantities: Record<string, number> = {};
      products.forEach((product) => {
        initialQuantities[product._id] = 1;
      });
      setQuantities(initialQuantities);
    }
  }, [products]);

  const handleQuantityChange = (productId: string, quantity: number) => {
    setQuantities((prev) => ({ ...prev, [productId]: quantity }));
  };

  const handleNext = () => {
    if (step === 1) navigate(`/checkout/${checkoutId}/shipping`);
    else if (step === 2) navigate(`/checkout/${checkoutId}/payment`);
  };

  const handlePaymentMethod = async (paymentMethodId: string, tipAmount?: number) => {
    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentMethodId,
          products: getCartItems().map((item) => ({
            productId: item.product._id,
            quantity: item.quantity,
          })),
          email: customerEmail,
          shipping: shippingAddress,
          total: getTotal(),
          tipAmount: tipAmount,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Payment failed");
      }

      navigate(`/checkout/${checkoutId}/success`, {
        state: {
          cartItems: getCartItems(),
          total: getTotal(),
          tipAmount: tipAmount ?? 0,
        },
      });
    } catch (err) {
      throw err;
    }
  };

  const canProceedToNextStep = () => {
    if (step === 1) return customerEmail.trim() !== "";
    if (step === 2) {
      return (
        products.length > 0 &&
        Object.keys(quantities).length > 0 &&
        isAddressComplete &&
        shippingAddress !== null
      );
    }
    return false;
  };

  const getCartItems = () => {
    return products
      .map((product) => ({
        product,
        quantity: quantities[product._id] || 0,
      }))
      .filter((item) => item.quantity > 0);
  };

  const getTotal = () => {
    return getCartItems().reduce(
      (sum, item) => sum + (item.product.price || 0) * item.quantity,
      0
    );
  };

  const getCustomerName = () => shippingAddress?.name ?? "";

  return (
    <div className="max-w-4xl w-full mx-auto min-h-screen flex flex-col py-4 px-4">
      <h1 className="text-2xl font-bold">{artistName || ""}</h1>
      <div className="flex items-center justify-center gap-0 py-4">
        {[1, 2, 3].map((s, i) => (
          <div key={s} className="flex items-center">
            {i > 0 && <div className="h-px w-8 shrink-0 bg-gray-300 mx-1" />}
            <button
              type="button"
              onClick={() => {
                if (!isProcessing && s < step) {
                  if (s === 1) navigate(`/checkout/${checkoutId}/email`);
                  else if (s === 2) navigate(`/checkout/${checkoutId}/shipping`);
                }
              }}
              disabled={isProcessing || s > step}
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                s === step
                  ? "bg-black text-white"
                  : s < step
                    ? "border border-gray-300 bg-white text-black hover:bg-gray-50 cursor-pointer"
                    : "border border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              {s}
            </button>
          </div>
        ))}
      </div>

      {step === 1 && (
        <CheckoutEmailStep
          email={customerEmail}
          onEmailChange={setCustomerEmail}
          products={products}
          quantities={quantities}
          onQuantityChange={handleQuantityChange}
          total={getTotal()}
        />
      )}

      {step === 2 && (
        <CheckoutShippingStep
          products={products}
          quantities={quantities}
          onQuantityChange={handleQuantityChange}
          shippingAddress={shippingAddress}
          onShippingAddressChange={setShippingAddress}
          onAddressCompleteChange={setIsAddressComplete}
          stripePromise={stripePromise}
          total={getTotal()}
        />
      )}

      {step === 3 && stripePromise && (
        <Elements stripe={stripePromise}>
          <CheckoutPaymentStep
            products={products}
            quantities={quantities}
            onQuantityChange={handleQuantityChange}
            cartItems={getCartItems()}
            total={getTotal()}
            name={getCustomerName()}
            email={customerEmail}
            onPaymentMethod={handlePaymentMethod}
            isProcessing={isProcessing}
            error={error}
            setError={setError}
            setIsProcessing={setIsProcessing}
            checkoutSettings={checkoutSettings}
          />
        </Elements>
      )}

      {step < 3 && (
        <div className="flex justify-end gap-2 pt-4 border-t mt-auto">
          <Button type="button" onClick={handleNext} disabled={!canProceedToNextStep()}>
            Continue
          </Button>
        </div>
      )}
    </div>
  );
}
